import {
  checkFit,
  compatibleModules,
  compileCatalog,
  pointDefinition,
  type CompiledCatalog,
} from "./catalog.ts";
import {
  applyBase,
  applyAtPoint,
  compileAssemblyPlan,
  normalizeRecipe,
} from "./recipe.ts";
import type { RecipeStore } from "./storage.ts";
import type {
  ActionOutcome,
  AssemblyPlan,
  AttachmentPointInstanceId,
  BuildRecipe,
  Catalog,
  CompatibleModuleChoice,
  ConstructorIntent,
  ConstructorSession,
  ConstructorSnapshot,
  GlobalLivery,
  OutcomeEffect,
  RejectionCode,
  RemovedSubtree,
  SavedMechSummary,
  TryOnView,
} from "./types.ts";

export interface CreateConstructorSessionOptions {
  readonly catalog: Catalog;
  readonly demonstrationRecipe: BuildRecipe;
  readonly store: RecipeStore;
}

export async function createConstructorSession(
  options: CreateConstructorSessionOptions,
): Promise<ConstructorSession> {
  const catalog = compileCatalog(options.catalog);
  const demonstrationRecipe = normalizeRecipe(
    options.demonstrationRecipe,
    catalog,
    options.demonstrationRecipe,
  );

  let current: BuildRecipe = demonstrationRecipe;
  try {
    const stored = await options.store.readCurrent();
    if (stored != null) current = normalizeRecipe(stored, catalog, demonstrationRecipe);
  } catch {
    // A broken read must not prevent the local, demonstration-first product from opening.
  }

  let savedMechs: readonly SavedMechSummary[] = [];
  try {
    savedMechs = await options.store.listNamed();
  } catch {
    // Named saves remain unavailable until the next successful refresh/write.
  }

  return new ConstructorSessionImpl(
    catalog,
    demonstrationRecipe,
    options.store,
    current,
    savedMechs,
  );
}

class ConstructorSessionImpl implements ConstructorSession {
  readonly #catalog: CompiledCatalog;
  readonly #demonstrationRecipe: BuildRecipe;
  readonly #store: RecipeStore;
  #recipe: BuildRecipe;
  #plan: AssemblyPlan;
  #selectedPointId: AttachmentPointInstanceId | null = null;
  #tryOn: TryOnView | null = null;
  #undo: BuildRecipe[] = [];
  #redo: BuildRecipe[] = [];
  #savedMechs: readonly SavedMechSummary[];
  #revision = 0;
  #viewRevision = 0;
  #tryOnSequence = 0;
  #listeners = new Set<(snapshot: ConstructorSnapshot) => void>();
  #lane: Promise<void> = Promise.resolve();
  #disposed = false;
  #currentSnapshot: ConstructorSnapshot;

  constructor(
    catalog: CompiledCatalog,
    demonstrationRecipe: BuildRecipe,
    store: RecipeStore,
    current: BuildRecipe,
    savedMechs: readonly SavedMechSummary[],
  ) {
    this.#catalog = catalog;
    this.#demonstrationRecipe = demonstrationRecipe;
    this.#store = store;
    this.#recipe = current;
    this.#plan = compileAssemblyPlan(current, catalog);
    this.#savedMechs = savedMechs;
    this.#currentSnapshot = this.#makeSnapshot();
  }

  snapshot(): ConstructorSnapshot {
    return this.#currentSnapshot;
  }

  dispatch(intent: ConstructorIntent): Promise<ActionOutcome> {
    const result = this.#lane.then(() => this.#handle(intent));
    this.#lane = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  subscribe(listener: (snapshot: ConstructorSnapshot) => void): () => void {
    if (this.#disposed) return () => undefined;
    this.#listeners.add(listener);
    listener(this.#currentSnapshot);
    return () => this.#listeners.delete(listener);
  }

  async dispose(): Promise<void> {
    if (this.#disposed) return;
    this.#disposed = true;
    await this.#lane;
    this.#listeners.clear();
    this.#tryOn = null;
  }

  async #handle(intent: ConstructorIntent): Promise<ActionOutcome> {
    if (this.#disposed) return this.#reject("DISPOSED", "Constructor session is disposed");
    switch (intent.kind) {
      case "select-point":
        return this.#selectPoint(intent.pointId);
      case "preview-module":
        return this.#previewModule(intent.moduleId);
      case "preview-base":
        return this.#previewBase(intent.moduleId);
      case "preview-recipe":
        return this.#previewRecipe(
          intent.recipe,
          intent.demonstrationMechId,
          intent.label,
        );
      case "confirm-preview":
        return this.#confirmPreview(intent.previewId, intent.acceptRemoval === true);
      case "cancel-preview":
        return this.#cancelPreview(intent.previewId);
      case "set-livery":
        return this.#setLivery(intent.livery);
      case "undo":
        return this.#undoChange();
      case "redo":
        return this.#redoChange();
      case "save-named":
        return this.#saveNamed(intent.name);
      case "load-named":
        return this.#loadNamed(intent.id);
      case "load-recipe":
        return this.#loadRecipe(intent.recipe);
    }
  }

  #selectPoint(pointId: AttachmentPointInstanceId | null): ActionOutcome {
    if (pointId != null && this.#plan.points.every((point) => point.instanceId !== pointId)) {
      return this.#reject(
        "ATTACHMENT_POINT_UNKNOWN",
        `Attachment point ${pointId} is not present in the confirmed assembly`,
      );
    }
    if (this.#selectedPointId === pointId && this.#tryOn == null) return this.#ok("no-change");
    this.#selectedPointId = pointId;
    this.#tryOn = null;
    this.#publishViewChange();
    return this.#ok("selected");
  }

  #previewModule(moduleId: string | null): ActionOutcome {
    const selected = this.#plan.points.find(
      (point) => point.instanceId === this.#selectedPointId,
    );
    if (selected == null) {
      return this.#reject(
        "ATTACHMENT_POINT_UNKNOWN",
        "Select an available attachment point before starting a Try-on",
      );
    }
    if (moduleId != null) {
      const module = this.#catalog.moduleById.get(moduleId);
      if (module == null) return this.#reject("MODULE_UNKNOWN", `Unknown module ${moduleId}`);
      if (module.role !== "module") {
        return this.#reject("WRONG_MODULE_ROLE", "A base cannot occupy a child attachment point");
      }
      const parentNode = this.#plan.nodes.find(
        (node) => node.positionId === selected.parentPositionId,
      )!;
      const parentModule = this.#catalog.moduleById.get(parentNode.moduleId)!;
      const point = pointDefinition(parentModule, selected.pointId)!;
      const fit = checkFit(point, module);
      if (!fit.ok) {
        if (fit.code === "EXPLICITLY_DENIED") {
          return this.#reject(
            "EXPLICITLY_DENIED",
            fit.rule.reason,
            fit.rule.ruleId,
          );
        }
        return this.#reject(fit.code, `Module ${moduleId} does not fit ${selected.pointId}`);
      }
    }

    const change = applyAtPoint(
      this.#recipe,
      selected.parentPositionId,
      selected.pointId,
      moduleId,
      this.#catalog,
    );
    if (sameRecipe(change.recipe, this.#recipe)) {
      this.#tryOn = null;
      this.#publishViewChange();
      return this.#ok("no-change");
    }
    const id = `try-on-${++this.#tryOnSequence}`;
    this.#tryOn = {
      id,
      targetKind: "attachment-point",
      pointId: selected.instanceId,
      moduleId,
      plan: compileAssemblyPlan(change.recipe, this.#catalog),
      consequences: change.consequences,
      requiresRemovalAcceptance: change.consequences.length > 0,
    };
    this.#publishViewChange();
    return this.#ok("previewed", { previewId: id });
  }

  #previewBase(moduleId: string): ActionOutcome {
    const module = this.#catalog.moduleById.get(moduleId);
    if (module == null) return this.#reject("MODULE_UNKNOWN", `Unknown module ${moduleId}`);
    if (module.role !== "base") {
      return this.#reject("WRONG_MODULE_ROLE", "Only a base module can replace the base");
    }

    const change = applyBase(this.#recipe, moduleId, this.#catalog);
    if (sameRecipe(change.recipe, this.#recipe)) {
      this.#tryOn = null;
      this.#publishViewChange();
      return this.#ok("no-change");
    }
    const id = `try-on-${++this.#tryOnSequence}`;
    this.#tryOn = {
      id,
      targetKind: "base",
      pointId: baseTryOnMarker(this.#recipe.base.positionId),
      moduleId,
      plan: compileAssemblyPlan(change.recipe, this.#catalog),
      consequences: change.consequences,
      requiresRemovalAcceptance: change.consequences.length > 0,
    };
    this.#publishViewChange();
    return this.#ok("previewed", { previewId: id });
  }

  #previewRecipe(
    incoming: BuildRecipe,
    demonstrationMechId: string,
    label: string,
  ): ActionOutcome {
    const recipe = normalizeRecipe(incoming, this.#catalog, this.#demonstrationRecipe);
    if (sameRecipe(recipe, this.#recipe)) {
      this.#tryOn = null;
      this.#publishViewChange();
      return this.#ok("no-change");
    }

    const id = `try-on-${++this.#tryOnSequence}`;
    const consequences = [currentAssemblyConsequence(this.#recipe)];
    this.#tryOn = {
      id,
      targetKind: "recipe",
      pointId: demonstrationMechTryOnMarker(demonstrationMechId),
      moduleId: null,
      demonstrationMechId,
      label,
      plan: compileAssemblyPlan(recipe, this.#catalog),
      consequences,
      requiresRemovalAcceptance: true,
    };
    this.#selectedPointId = null;
    this.#publishViewChange();
    return this.#ok("previewed", { previewId: id });
  }

  async #confirmPreview(previewId: string, acceptRemoval: boolean): Promise<ActionOutcome> {
    if (this.#tryOn == null) {
      return this.#reject("TRY_ON_MISSING", "There is no active Try-on to confirm");
    }
    if (this.#tryOn.id !== previewId) {
      return this.#reject("TRY_ON_STALE", `Try-on ${previewId} is no longer active`);
    }
    if (this.#tryOn.requiresRemovalAcceptance && !acceptRemoval) {
      return this.#reject(
        "REMOVAL_ACCEPTANCE_REQUIRED",
        "Confirm the reported descendant removals before applying this Try-on",
      );
    }
    return this.#commit(this.#recipeFromPlan(this.#tryOn.plan), "committed");
  }

  #cancelPreview(previewId: string): ActionOutcome {
    if (this.#tryOn == null) {
      return this.#reject("TRY_ON_MISSING", "There is no active Try-on to cancel");
    }
    if (this.#tryOn.id !== previewId) {
      return this.#reject("TRY_ON_STALE", `Try-on ${previewId} is no longer active`);
    }
    this.#tryOn = null;
    this.#publishViewChange();
    return this.#ok("cancelled");
  }

  async #setLivery(livery: GlobalLivery): Promise<ActionOutcome> {
    const next = { ...this.#recipe, livery: { ...livery } };
    return this.#commit(next, "committed");
  }

  async #undoChange(): Promise<ActionOutcome> {
    const next = this.#undo.at(-1);
    if (next == null) return this.#reject("NOTHING_TO_UNDO", "There is nothing to undo");
    const persisted = await this.#persist(next);
    if (persisted != null) return persisted;
    this.#undo = this.#undo.slice(0, -1);
    this.#redo = [...this.#redo, this.#recipe];
    this.#replaceConfirmed(next);
    return this.#ok("undone");
  }

  async #redoChange(): Promise<ActionOutcome> {
    const next = this.#redo.at(-1);
    if (next == null) return this.#reject("NOTHING_TO_REDO", "There is nothing to redo");
    const persisted = await this.#persist(next);
    if (persisted != null) return persisted;
    this.#redo = this.#redo.slice(0, -1);
    this.#undo = bounded([...this.#undo, this.#recipe]);
    this.#replaceConfirmed(next);
    return this.#ok("redone");
  }

  async #saveNamed(rawName: string): Promise<ActionOutcome> {
    const name = rawName.trim();
    if (name.length === 0) return this.#reject("SAVE_NAME_REQUIRED", "Saved mech needs a name");
    try {
      const saved = await this.#store.writeNamed(name, this.#recipe);
      this.#savedMechs = replaceSaved(this.#savedMechs, saved);
      this.#publishViewChange();
      return this.#ok("saved", { saved });
    } catch {
      return this.#reject("STORAGE_WRITE_FAILED", "Could not write the named mech");
    }
  }

  async #loadNamed(id: string): Promise<ActionOutcome> {
    let recipe: BuildRecipe | null;
    try {
      recipe = await this.#store.readNamed(id);
    } catch {
      return this.#reject("STORAGE_READ_FAILED", "Could not read the named mech");
    }
    if (recipe == null) {
      return this.#reject("SAVED_MECH_NOT_FOUND", `Saved mech ${id} does not exist`);
    }
    return this.#loadRecipe(recipe);
  }

  async #loadRecipe(incoming: BuildRecipe): Promise<ActionOutcome> {
    const next = normalizeRecipe(incoming, this.#catalog, this.#demonstrationRecipe);
    const outcome = await this.#commit(next, "loaded");
    return outcome;
  }

  async #commit(next: BuildRecipe, effect: "committed" | "loaded"): Promise<ActionOutcome> {
    if (sameRecipe(next, this.#recipe)) {
      if (this.#tryOn != null) {
        this.#tryOn = null;
        this.#publishViewChange();
      }
      return this.#ok("no-change");
    }
    const persisted = await this.#persist(next);
    if (persisted != null) return persisted;
    this.#undo = bounded([...this.#undo, this.#recipe]);
    this.#redo = [];
    this.#replaceConfirmed(next);
    return this.#ok(effect);
  }

  async #persist(next: BuildRecipe): Promise<ActionOutcome | null> {
    try {
      await this.#store.writeCurrent(next);
      return null;
    } catch {
      return this.#reject(
        "STORAGE_WRITE_FAILED",
        "The confirmed assembly was not changed because local persistence failed",
      );
    }
  }

  #replaceConfirmed(next: BuildRecipe): void {
    this.#recipe = structuredClone(next);
    this.#plan = compileAssemblyPlan(this.#recipe, this.#catalog);
    this.#tryOn = null;
    if (
      this.#selectedPointId != null &&
      this.#plan.points.every((point) => point.instanceId !== this.#selectedPointId)
    ) {
      this.#selectedPointId = null;
    }
    this.#revision += 1;
    this.#publishViewChange();
  }

  #recipeFromPlan(plan: AssemblyPlan): BuildRecipe {
    const baseNode = plan.nodes.find((node) => node.parentPositionId == null)!;
    return {
      schema: 1,
      catalogFingerprint: this.#catalog.source.fingerprint,
      base: { positionId: baseNode.positionId, moduleId: baseNode.moduleId },
      placements: plan.nodes
        .filter(
          (node): node is typeof node & {
            parentPositionId: string;
            attachmentPointId: string;
          } => node.parentPositionId != null && node.attachmentPointId != null,
        )
        .map((node) => ({
          positionId: node.positionId,
          parentPositionId: node.parentPositionId,
          attachmentPointId: node.attachmentPointId,
          moduleId: node.moduleId,
        })),
      unresolved: plan.unresolved.map((position) => structuredClone(position)),
      livery: { ...plan.livery },
    };
  }

  #makeSnapshot(): ConstructorSnapshot {
    const selected = this.#plan.points.find(
      (point) => point.instanceId === this.#selectedPointId,
    );
    let choices: readonly CompatibleModuleChoice[] = [];
    if (selected != null) {
      const parent = this.#plan.nodes.find(
        (node) => node.positionId === selected.parentPositionId,
      )!;
      const parentModule = this.#catalog.moduleById.get(parent.moduleId)!;
      const point = pointDefinition(parentModule, selected.pointId)!;
      choices = compatibleModules(this.#catalog, point).map((module) => ({
        moduleId: module.id,
        label: module.label,
        visualRef: module.visualRef,
      }));
    }
    return freezeDeep({
      revision: this.#revision,
      viewRevision: this.#viewRevision,
      recipe: structuredClone(this.#recipe),
      plan: structuredClone(this.#plan),
      displayPlan: structuredClone(this.#tryOn?.plan ?? this.#plan),
      selectedPointId: this.#selectedPointId,
      compatibleModules: choices,
      tryOn: this.#tryOn == null ? null : structuredClone(this.#tryOn),
      canUndo: this.#undo.length > 0,
      canRedo: this.#redo.length > 0,
      savedMechs: this.#savedMechs.map((saved) => ({ ...saved })),
    });
  }

  #publishViewChange(): void {
    this.#viewRevision += 1;
    this.#currentSnapshot = this.#makeSnapshot();
    for (const listener of this.#listeners) {
      try {
        listener(this.#currentSnapshot);
      } catch {
        // One view must not break serialized state changes for other subscribers.
      }
    }
  }

  #ok(
    effect: OutcomeEffect,
    extras: { previewId?: string; saved?: SavedMechSummary } = {},
  ): ActionOutcome {
    return { ok: true, effect, revision: this.#revision, ...extras };
  }

  #reject(code: RejectionCode, message: string, denyRuleId?: string): ActionOutcome {
    const base = { ok: false as const, code, message, revision: this.#revision };
    return denyRuleId == null ? base : { ...base, denyRuleId };
  }
}

function sameRecipe(left: BuildRecipe, right: BuildRecipe): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function baseTryOnMarker(positionId: string): AttachmentPointInstanceId {
  return `@base::${encodeURIComponent(positionId)}`;
}

function demonstrationMechTryOnMarker(
  demonstrationMechId: string,
): AttachmentPointInstanceId {
  return `@demonstration-mech::${encodeURIComponent(demonstrationMechId)}`;
}

function currentAssemblyConsequence(recipe: BuildRecipe): RemovedSubtree {
  const positionIds = [
    recipe.base.positionId,
    ...recipe.placements.map((placement) => placement.positionId),
  ];
  return {
    rootPositionId: recipe.base.positionId,
    rootModuleId: recipe.base.moduleId,
    positionIds,
    moduleCount: positionIds.length,
  };
}

function bounded(history: readonly BuildRecipe[]): BuildRecipe[] {
  return history.slice(-100).map((recipe) => structuredClone(recipe));
}

function replaceSaved(
  current: readonly SavedMechSummary[],
  saved: SavedMechSummary,
): readonly SavedMechSummary[] {
  return [...current.filter((candidate) => candidate.id !== saved.id), saved].sort((left, right) =>
    left.name.localeCompare(right.name),
  );
}

function freezeDeep<T>(value: T): T {
  if (value == null || typeof value !== "object" || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) freezeDeep(child);
  return Object.freeze(value);
}
